import React, { useState } from "react";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaTwitter,
  FaInstagram,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

export default function Blogs() {
  const navigate = useNavigate();
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const blogs = [
    {
      id: 1,
      title: "AI-Powered Assessments in NextTestify",
      date: "05 Mar 2026",
      image: "https://images.unsplash.com/photo-1581092334651-ddf26d9a09d0?q=80&w=400",
      hero: "https://images.unsplash.com/photo-1581092334651-ddf26d9a09d0?q=80&w=1400",
      content: "AI-Powered Assessments in NextTestify uses intelligent algorithms to automatically generate and evaluate exam questions based on syllabus and learning objectives. It ensures fast, accurate, and unbiased grading for both objective and descriptive answers. This system helps educators save time while delivering a more consistent and personalized assessment experience for students.",
      sections: [
        {
          title: "Smart Question Generation",
          image: "https://images.unsplash.com/photo-1600195077909-46e573870d99?q=80&w=1200",
          text: "NextTestify uses AI models to generate high-quality assessment questions based on syllabus patterns. It understands topics, difficulty levels, and learning outcomes to create balanced question sets. Teachers can instantly generate quizzes without manual effort. It reduces repetitive workload and ensures question variety. This helps maintain consistency and academic relevance across exams."
        },
        {
          title: "Automated Evaluation",
          image: "https://images.unsplash.com/photo-1559028012-481c04fa702d?q=80&w=1200",
          text: "The platform automatically evaluates answers using natural language processing and machine learning models. It provides instant feedback for objective questions and intelligent scoring for descriptive answers. This reduces grading time by up to 80% while maintaining accuracy and eliminating human bias."
        }
      ]
    },
    {
      id: 2,
      title: "Ensuring Secure Online Exams",
      date: "18 Feb 2026",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=400",
      hero: "https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=1400",
      content: "NextTestify provides enterprise-grade security features to ensure exam integrity and prevent cheating. From AI-based proctoring to secure browser lockdown, our platform offers multiple layers of protection for high-stakes assessments.",
      sections: [
        {
          title: "Access Control",
          image: "https://images.unsplash.com/photo-1510511459019-5dda7724fd87?q=80&w=1200",
          text: "Only authorized users can access exams. Our platform supports multi-factor authentication, role-based access control, and secure login protocols. Administrators can set specific time windows and IP restrictions to further enhance security."
        },
        {
          title: "Live Monitoring",
          image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=1200",
          text: "Prevent malpractice with real-time tracking and AI-powered proctoring. The system monitors unusual behavior, detects unauthorized devices, and flags suspicious activities. Recordings and logs are available for post-exam review."
        }
      ]
    },
    {
      id: 3,
      title: "Smart Exam Management System",
      date: "10 Jan 2026",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=400",
      hero: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1400",
      content: "Manage exams efficiently with scheduling automation, candidate management, and comprehensive dashboards. NextTestify streamlines the entire exam lifecycle from creation to results publication.",
      sections: [
        {
          title: "Scheduling",
          image: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?q=80&w=1200",
          text: "Automate exam scheduling with our intelligent calendar system. Set dates, time limits, and availability windows. The system automatically notifies candidates and handles rescheduling requests."
        },
        {
          title: "Dashboard",
          image: "https://images.unsplash.com/photo-1559028012-481c04fa702d?q=80&w=1200",
          text: "Centralized management dashboard provides a complete overview of all exams, candidates, and results. Track progress, monitor completion rates, and access detailed reports from a single interface."
        }
      ]
    },
    {
      id: 4,
      title: "Analytics & Performance Insights",
      date: "22 Dec 2025",
      image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?q=80&w=400",
      hero: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?q=80&w=1400",
      content: "Track performance and improve outcomes with advanced analytics. NextTestify provides actionable insights into candidate performance, question difficulty, and learning gaps.",
      sections: [
        {
          title: "Performance Tracking",
          image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200",
          text: "Monitor student progress with detailed performance metrics. Track scores by topic, question type, and difficulty level. Identify strengths and weaknesses at individual and group levels."
        },
        {
          title: "Insights",
          image: "https://images.unsplash.com/photo-1559028012-481c04fa702d?q=80&w=1200",
          text: "Use data to improve decisions. Generate comprehensive analytics reports, identify trends, and make data-driven improvements to your assessment strategies."
        }
      ]
    }
  ];

  const handleBlogClick = (blog) => {
    setSelectedBlog(blog);
    setShowDetail(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToList = () => {
    setShowDetail(false);
    setSelectedBlog(null);
  };

  // Detail View Component
  if (showDetail && selectedBlog) {
    return (
      <div className="relative min-h-screen mt-5 text-white overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=1600&auto=format&fit=crop"
          alt="bg"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a]/90 via-[#1e3a8a]/92 to-[#0f172a]/90 backdrop-blur-2xl"></div>
        
        <div className="relative z-10 py-20 px-4 md:px-10">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={handleBackToList}
              className="mb-6 flex items-center gap-2 text-blue-300 hover:text-blue-200 transition-colors"
            >
              ← Back to all blogs
            </button>
            
            <p className="text-sm uppercase tracking-widest text-gray-300 mb-2">
              {selectedBlog.date}
            </p>
            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              {selectedBlog.title}
            </h1>
            
            <img
              src={selectedBlog.hero}
              className="rounded-3xl mb-8 w-full h-[420px] object-cover brightness-90 contrast-110"
              alt={selectedBlog.title}
            />
            
            <p className="text-gray-300 mb-10 leading-relaxed text-lg">
              {selectedBlog.content}
            </p>
            
            {selectedBlog.sections.map((section, idx) => (
              <div key={idx} className={`grid md:grid-cols-2 gap-8 items-center mb-14 ${idx % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                <img
                  src={section.image}
                  className="rounded-xl w-full h-[300px] object-cover brightness-90 contrast-110"
                  alt={section.title}
                />
                <div>
                  <h2 className="text-2xl font-semibold mb-4">{section.title}</h2>
                  <p className="text-gray-300 leading-relaxed">{section.text}</p>
                </div>
              </div>
            ))}
            
           
          </div>
        </div>
      </div>
    );
  }

  // Main Blog List View
  return (
    <div className="relative min-h-screen mt-5 text-white overflow-hidden">
      <img
        src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=1600&auto=format&fit=crop"
        alt="bg"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a]/90 via-[#1e3a8a]/92 to-[#0f172a]/90 backdrop-blur-2xl"></div>

      <div className="relative z-10 py-20 px-4 md:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
          {/* LEFT CONTENT - Featured Blog */}
          <div className="lg:col-span-2">
            <p className="text-3xl uppercase tracking-widest text-gray-300 mb-2">
              Blogs
            </p>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Transforming Digital Assessments with NextTestify
            </h1>

            <img
              src="https://images.unsplash.com/photo-1555949963-aa79dcee981c?q=80&w=1400&auto=format&fit=crop"
              className="rounded-3xl mb-8 w-full h-[420px] object-cover brightness-90 contrast-110"
              alt="Featured"
            />

            <p className="text-gray-300 mb-6 leading-relaxed text-lg">
              NextTestify empowers educators and organizations with smart assessment tools 
              that simplify exam creation, management, and evaluation. In today's digital-first 
              environment, traditional assessment methods are no longer efficient or scalable...
            </p>
            
            <button
              onClick={() => handleBlogClick(blogs[0])}
              className="group inline-flex items-center gap-2 px-6 py-3 bg-blue-600/30 hover:bg-blue-600/50 rounded-xl transition-all duration-300"
            >
              <span>Read Full Article</span>
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>
          </div>

          {/* RIGHT SIDEBAR - Blog Chips */}
          <div className="space-y-6 lg:ml-6">
            {/* Share Section */}
            <div className="bg-white/10 backdrop-blur-xl p-4 rounded-xl border border-white/10">
              <h3 className="font-semibold mb-4">Share on Social Media</h3>
              <div className="flex gap-4 text-lg text-gray-300">
                <FaInstagram className="cursor-pointer hover:text-white transition-colors" />
                <FaTwitter className="cursor-pointer hover:text-white transition-colors" />
                <FaFacebookF className="cursor-pointer hover:text-white transition-colors" />
                <FaLinkedinIn className="cursor-pointer hover:text-white transition-colors" />
              </div>
            </div>

            {/* Explore More Blogs - Chips */}
            <div className="bg-white/10 backdrop-blur-xl p-4 rounded-xl border border-white/10">
              <h3 className="font-semibold mb-4">Explore More Blogs</h3>
              <div className="space-y-3">
                {blogs.map((blog) => (
                  <div
                    key={blog.id}
                    onClick={() => handleBlogClick(blog)}
                    className="group flex gap-3 p-3 rounded-lg cursor-pointer transition-all duration-300 hover:bg-white/20 active:scale-[0.98]"
                  >
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="w-20 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium group-hover:text-blue-300 transition-colors line-clamp-2">
                        {blog.title}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">{blog.date}</p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                        {blog.content.substring(0, 60)}...
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}